import { Box, Typography, CircularProgress } from "@mui/material";
import { useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { useAppDispatch } from "@/app/hooks";
import { fetchBaseQueryWithEncryption } from "@/features/fetchBaseQueryWithEncryption";
import {
  setCurrentUser,
  setRoleCodeDetails,
  setUserProfile,
} from "@/features/Global/slice";
import { roleAuthMgmt } from "@/features/RoleAuthMgmt/api";

const AUTH_CALLBACK_PATH = "/auth/callback";

const OauthLoginCallback = () => {
  const { search } = useLocation();
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const [error, setError] = useState<string>("");
  const [isLoading, setIsLoading] = useState(true);

  const applyRolePermissions = (roleData: any) => {
    const updatedPermissions = roleData?.permissionList || [];
    dispatch(setRoleCodeDetails(updatedPermissions));
  };

  useEffect(() => {
    const completeLogin = async () => {
      const queryParams = new URLSearchParams(search);
      const code = queryParams.get("code");
      // const stateParam = queryParams.get("state");
      const redirectPath = "/";

      if (!code) {
        setError("Missing authorization code.");
        setIsLoading(false);
        return;
      }

      try {
        const response = await fetch(
          `${import.meta.env.VITE_COMPANY_API_URL}/auth/cognito/callback`,
          {
            method: "POST",
            headers: {
              "Content-Type": "application/json",
            },
            body: JSON.stringify({
              code,
                redirectUri: `${window.location.origin}${AUTH_CALLBACK_PATH}`,
            }),
            credentials: "include",
          },
        );

        const result = await response.json();

        if (!result) {
          throw new Error("Missing auth response.");
        }

        // Normal defect-tracker-web flow below (only when no external redirect)
        const userObject = result?.user;
        localStorage.setItem("userId", userObject.userId);
        const userDetailsResponse = await fetchBaseQueryWithEncryption(
          `${import.meta.env.VITE_COMPANY_API_URL}/user-mgmt/details-user/${userObject.userId}`,
          {
            method: "GET",
            headers: {
              "Content-Type": "application/json",
            },
          },
        );

        const userProfile = userDetailsResponse?.data?.decryptedData;

        const roleCodeFromList = userProfile?.invitedCompanies?.[0]?.roleCode;

        if (result?.accessToken) {
          localStorage.setItem("accessToken", result.accessToken);
        }
        if (result?.refreshToken) {
          localStorage.setItem("refreshToken", result.refreshToken);
        }
        if (userProfile) {
          dispatch(setCurrentUser(userProfile));
          dispatch(setUserProfile({ userProfile }));
          if (userProfile.companyId) {
            localStorage.setItem("compId", userProfile.companyId);
          }
          if (userProfile.lastVisitedTeam) {
            localStorage.setItem(
              "lastVisitedTeam",
              userProfile.lastVisitedTeam,
            );
          }
          if (userProfile.lastVisitedProject) {
            localStorage.setItem(
              "lastVisitedProject",
              userProfile.lastVisitedProject,
            );
          }
        }
        if (roleCodeFromList) {
          localStorage.setItem("userRoleCode", roleCodeFromList);
          const response = await dispatch(
            roleAuthMgmt.endpoints.getRoleCodeDetails.initiate(
              roleCodeFromList,
            ),
          );
          if (response?.data?.data?.decryptedData) {
            applyRolePermissions(response?.data?.data?.decryptedData);
          }
        }

        navigate(redirectPath, { replace: true });
      } catch (err) {
        console.error("OAuth callback failed:", err);
        setError("Failed to complete sign-in.");
        setIsLoading(false);
      }
    };

    completeLogin();
  }, [dispatch, navigate, search]);

  if (error) {
    return (
      <Box sx={{ p: 2, color: "error.main" }}>
        <Typography variant="h6">Error</Typography>
        <Typography>{error}</Typography>
      </Box>
    );
  }

  return (
    <Box
      sx={{
        p: 2,
        bgcolor: "background.paper",
        borderRadius: 1,
        maxWidth: "100%",
        overflow: "auto",
        minHeight: 120,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        gap: 2,
      }}
    >
      <CircularProgress size={22} />
      <Typography>
        {isLoading ? "Completing sign-in..." : "Redirecting..."}
      </Typography>
    </Box>
  );
};

export default OauthLoginCallback;
